import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PaperPlaneTilt, ArrowClockwise } from "@phosphor-icons/react";

// Test Send + Retry Queue + Delivery Logs — the three read-only diagnostic
// panels that sit below the main settings form. All state is owned by the
// parent so we can trigger refreshes after form saves without lifting state.
export default function WATestAndLogsPanel({
  provider,
  testNumber, setTestNumber, testing, onTest,
  retryQueue, retryStats, retryFilter, setRetryFilter, onRefreshQueue,
  onForceRetry, onDeleteRetryItem,
  logs, onRefreshLogs,
}) {
  return (
    <>
      {/* Test Send */}
      <section className="border border-border bg-card p-5 space-y-4 mt-8">
        <div>
          <div className="overline">Test Kirim</div>
          <div className="text-xs text-muted-foreground mt-1">
            Kirim pesan template ke nomor Anda sendiri untuk memastikan integrasi berjalan. Bisa dijalankan meski toggle "Aktifkan" masih OFF.
          </div>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-sm mb-2 block">
              {provider === "telegram" ? "Chat ID Tujuan Test" : "Nomor Tujuan Test"}
            </Label>
            <Input
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder={provider === "telegram" ? "-1001234567890 atau chat_id pribadi" : "08xxxxxxxxxx"}
              className="rounded-xl h-11 font-mono"
              data-testid="wa-test-number"
            />
          </div>
          <Button
            type="button"
            onClick={onTest}
            disabled={testing}
            className="rounded-xl h-11 font-bold uppercase"
            data-testid="wa-test-button"
          >
            <PaperPlaneTilt size={16} className="mr-2" />
            {testing ? "Mengirim..." : "Kirim Test"}
          </Button>
        </div>
      </section>

      {/* Retry Queue */}
      <section className="border border-border bg-card p-5 space-y-3 mt-8" data-testid="retry-queue-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="overline">Antrian Retry Otomatis</div>
            <div className="text-xs text-muted-foreground mt-1">
              Pesan yang gagal terkirim otomatis diulang dengan jeda bertingkat (30 dtk → 2 mnt → 5 mnt → 15 mnt → 30 mnt → 1 jam → 3 jam → 6 jam → 12 jam → 24 jam · maks 10 percobaan / ~22 jam).
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="bg-yellow-100 text-yellow-800 border border-yellow-300 px-2 py-1 font-mono">
              Pending: <strong>{retryStats.pending}</strong>
            </span>
            <span className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-2 py-1 font-mono">
              Success: <strong>{retryStats.success}</strong>
            </span>
            <span className="bg-red-100 text-red-800 border border-red-300 px-2 py-1 font-mono">
              Gave Up: <strong>{retryStats.permanent_failure}</strong>
            </span>
            <Button type="button" variant="outline" size="sm" className="rounded-xl"
                    onClick={onRefreshQueue} data-testid="retry-queue-refresh">
              <ArrowClockwise size={14} />
            </Button>
          </div>
        </div>
        <div className="flex gap-1 border-b border-border">
          {[
            { key: "pending", label: "Menunggu", count: retryStats.pending },
            { key: "permanent_failure", label: "Gave Up", count: retryStats.permanent_failure },
            { key: "all", label: "Semua", count: retryStats.total },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setRetryFilter(tab.key)}
              className={`px-3 py-2 text-xs uppercase tracking-wider border-b-2 -mb-px ${
                retryFilter === tab.key
                  ? "border-primary text-foreground font-bold"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`retry-tab-${tab.key}`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        {retryQueue.length === 0 ? (
          <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border">
            Tidak ada entry di queue {retryFilter !== "all" ? `(status "${retryFilter}")` : ""}.
          </div>
        ) : (
          <div className="border border-border max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-secondary/60 sticky top-0 text-left">
                <tr>
                  <th className="p-2 overline">Target</th>
                  <th className="p-2 overline">Provider</th>
                  <th className="p-2 overline">Status</th>
                  <th className="p-2 overline">Attempt</th>
                  <th className="p-2 overline">Retry Berikutnya</th>
                  <th className="p-2 overline">Error Terakhir</th>
                  <th className="p-2 overline text-right">Aksi</th>
                </tr>
              </thead>
              <tbody>
                {retryQueue.map((r) => {
                  // Style/label lookups for status pill and next-retry timestamp
                  const statusStyle = {
                    pending: "bg-yellow-100 text-yellow-800",
                    success: "bg-emerald-100 text-emerald-800",
                  }[r.status] || "bg-red-100 text-red-800";
                  const statusLabel = {
                    pending: "menunggu",
                    success: "sukses",
                  }[r.status] || "gave up";
                  const fmt = (iso) => new Date(iso).toLocaleString("id-ID");
                  let nextRetryCell = "-";
                  if (r.status === "pending" && r.next_retry_at) nextRetryCell = fmt(r.next_retry_at);
                  else if (r.resolved_at) nextRetryCell = `✔ ${fmt(r.resolved_at)}`;
                  else if (r.gave_up_at) nextRetryCell = `✘ ${fmt(r.gave_up_at)}`;
                  return (
                  <tr key={r.id} className="border-t border-border/40 hover:bg-secondary/20" data-testid={`retry-row-${r.id}`}>
                    <td className="p-2 font-mono max-w-[180px] truncate" title={r.targets?.join(", ")}>
                      {(r.targets || []).slice(0, 2).join(", ")}
                      {(r.targets?.length || 0) > 2 && ` +${r.targets.length - 2}`}
                    </td>
                    <td className="p-2 uppercase">{r.provider}</td>
                    <td className="p-2">
                      <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusStyle}`}>
                        {statusLabel}
                      </span>
                    </td>
                    <td className="p-2 font-mono">{r.attempts}/{r.max_attempts}</td>
                    <td className="p-2 font-mono text-[11px]">
                      {nextRetryCell}
                    </td>
                    <td className="p-2 text-muted-foreground max-w-[200px] truncate" title={r.last_error}>
                      {r.last_error || "-"}
                    </td>
                    <td className="p-2 text-right whitespace-nowrap">
                      {(r.status === "pending" || r.status === "permanent_failure") && (
                        <button
                          type="button"
                          onClick={() => onForceRetry(r.id)}
                          className="text-primary hover:underline mr-2"
                          data-testid={`retry-force-${r.id}`}
                          title="Coba kirim sekarang"
                        >
                          Retry
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => onDeleteRetryItem(r.id)}
                        className="text-destructive hover:underline"
                        data-testid={`retry-delete-${r.id}`}
                        title="Hapus dari queue"
                      >
                        Hapus
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Logs */}
      <section className="border border-border bg-card p-5 space-y-3 mt-8">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Riwayat Pengiriman (30 terakhir)</div>
            <div className="text-xs text-muted-foreground mt-1">
              Log otomatis tiap upaya kirim, sukses & gagal.
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-xl"
            onClick={onRefreshLogs}
            data-testid="wa-logs-refresh"
          >
            <ArrowClockwise size={14} />
          </Button>
        </div>
        <div className="border border-border max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-secondary/60 sticky top-0 text-left">
              <tr>
                <th className="p-2 overline">Waktu</th>
                <th className="p-2 overline">Target</th>
                <th className="p-2 overline">Provider</th>
                <th className="p-2 overline">Status</th>
                <th className="p-2 overline">Response</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l._id} className="border-t border-border/70">
                  <td className="p-2 font-mono">{(l.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                  <td className="p-2 font-mono">{l.target}</td>
                  <td className="p-2 uppercase">{l.provider}</td>
                  <td className="p-2">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase ${
                      l.status === "success"
                        ? "bg-emerald-100 text-emerald-800 border-emerald-500"
                        : "bg-red-100 text-red-800 border-red-500"
                    }`}>
                      {l.status}
                    </span>
                  </td>
                  <td className="p-2 text-muted-foreground max-w-[280px] truncate" title={l.response}>{l.response}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">Belum ada aktivitas pengiriman.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
